/**
 * Default values for Closing Disclosure forms and data
 */

import type { LineItem, LineItemSection, LoanTerms, Party, Transaction } from '../types';

/**
 * Default empty transaction
 */
export const DEFAULT_TRANSACTION: Omit<Transaction, 'id' | 'created_at' | 'updated_at'> = {
  property_address: '',
  sale_price: undefined,
  closing_date: undefined,
  disbursement_date: undefined,
  settlement_agent_name: '',
  file_number: '',
  status: 'draft',
};

/**
 * Default empty loan terms
 */
export const DEFAULT_LOAN_TERMS: Omit<LoanTerms, 'id' | 'transaction_id'> = {
  loan_amount: 0,
  loan_term_months: 360, // 30 years
  loan_purpose: 'purchase',
  loan_product: '',
  loan_type: 'conventional',
  interest_rate: 0,
  monthly_principal_and_interest: 0,
  prepayment_penalty: false,
  balloon_payment: false,
  can_increase_after_closing: false,
  has_escrow: true,
  escrowed_items: [],
  estimated_total_monthly_payment: 0,
};

/**
 * Default empty party
 */
export const DEFAULT_PARTY: Omit<Party, 'id'> = {
  party_type: 'borrower',
  name: '',
  email: '',
  phone: '',
  nmls: '',
};

/**
 * Default empty line item
 */
export const DEFAULT_LINE_ITEM: Omit<LineItem, 'id' | 'transactionId'> = {
  section: 'A' as LineItemSection,
  description: '',
  borrowerPaidAtClosing: 0,
};

/**
 * Loan term options in months
 */
export const LOAN_TERM_OPTIONS = [
  { value: 60, label: '5 years' },
  { value: 84, label: '7 years' },
  { value: 120, label: '10 years' },
  { value: 180, label: '15 years' },
  { value: 240, label: '20 years' },
  { value: 300, label: '25 years' },
  { value: 360, label: '30 years' },
  { value: 480, label: '40 years' },
];

/**
 * Loan purpose options
 */
export const LOAN_PURPOSE_OPTIONS = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'refinance', label: 'Refinance' },
  { value: 'construction', label: 'Construction' },
  { value: 'other', label: 'Other' },
];

/**
 * Loan type options
 */
export const LOAN_TYPE_OPTIONS = [
  { value: 'conventional', label: 'Conventional' },
  { value: 'fha', label: 'FHA' },
  { value: 'va', label: 'VA' },
  { value: 'usda', label: 'USDA' },
  { value: 'other', label: 'Other' },
];

/**
 * Party type options
 */
export const PARTY_TYPE_OPTIONS = [
  { value: 'borrower', label: 'Borrower' },
  { value: 'seller', label: 'Seller' },
  { value: 'lender', label: 'Lender' },
  { value: 'mortgage_broker', label: 'Mortgage Broker' },
  { value: 'real_estate_broker_buyer', label: 'Real Estate Broker (Buyer)' },
  { value: 'real_estate_broker_seller', label: 'Real Estate Broker (Seller)' },
  { value: 'settlement_agent', label: 'Settlement Agent' },
];

/**
 * Transaction status options
 */
export const TRANSACTION_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', description: 'Initial draft, still being prepared' },
  { value: 'reconciling', label: 'Reconciling', description: 'Under review and reconciliation' },
  { value: 'approved', label: 'Approved', description: 'Approved and ready for closing' },
  { value: 'finalized', label: 'Finalized', description: 'Transaction completed and finalized' },
];

/**
 * Common escrowed item types
 */
export const ESCROW_ITEM_TYPES = [
  "Homeowner's Insurance",
  'Mortgage Insurance',
  'Property Taxes',
  'Flood Insurance',
  'HOA Dues',
];

/**
 * Wizard step configuration
 */
export const WIZARD_STEPS = [
  { id: 'basics', title: 'Transaction Basics', description: 'Property and closing details' },
  { id: 'loanTerms', title: 'Loan Terms', description: 'Loan details and features' },
  { id: 'parties', title: 'Parties', description: 'Borrower, seller, and professionals' },
  { id: 'closingCosts', title: 'Closing Costs', description: 'Fees and costs breakdown' },
  { id: 'sellerCredits', title: 'Seller Credits', description: 'Credits and adjustments' },
  { id: 'review', title: 'Review', description: 'Review and finalize' },
];
